import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  listInstallments,
  deleteInstallment,
  getInstallmentsSummary,
  loadInstallmentOptions,
  COURSE_OPTIONS,
  BATCH_OPTIONS,
  COMPANY_OPTIONS,
  BRANCH_OPTIONS,
  SCHEME_OPTIONS,
  ACADEMIC_YEAR_OPTIONS,
  SUBJECT_OPTIONS,
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  DATE_PERIOD_OPTIONS,
} from '../api/installmentsApi'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { appToast } from '../../../../components/AppToast'

const DEFAULT_PAGE_SIZE = 25

export function useInstallmentsListScreen() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Sub-tab (Active | Archive | All) synced to ?tab=
  const [tab, setTab] = useState(searchParams.get('tab') || 'active')

  // Filter states
  const [company, setCompany] = useState('Select')
  const [branch, setBranch] = useState('Select')
  const [course, setCourse] = useState('Select')
  const [scheme, setScheme] = useState('Select')
  const [academicYear, setAcademicYear] = useState('Select')
  const [batch, setBatch] = useState('Select')
  const [subject, setSubject] = useState('Select')
  const [gender, setGender] = useState('Select')
  const [searchField, setSearchField] = useState('Name')
  const [searchText, setSearchText] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [datePeriod, setDatePeriod] = useState('period')
  const [status, setStatus] = useState('All')
  const [paymentStatus, setPaymentStatus] = useState('All')

  // Interactive KPI card filter: 'All' | 'Paid' | 'Balance' | 'Overdue'
  const [kpiFilter, setKpiFilter] = useState('All')

  // Settings modal
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Options state
  const [options, setOptions] = useState({
    companyOptions: COMPANY_OPTIONS,
    branchOptions: BRANCH_OPTIONS,
    courseOptions: COURSE_OPTIONS,
    schemeOptions: SCHEME_OPTIONS,
    academicYearOptions: ACADEMIC_YEAR_OPTIONS,
    batchOptions: BATCH_OPTIONS,
    subjectOptions: SUBJECT_OPTIONS,
    genderOptions: GENDER_OPTIONS,
    statusOptions: STATUS_OPTIONS,
    paymentStatusOptions: PAYMENT_STATUS_OPTIONS,
    datePeriodOptions: DATE_PERIOD_OPTIONS,
    pageSizeOptions: [25, 50, 75, 100],
  })

  // Data
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // KPI summary (Fees / Receipts / Balance / Overdue)
  const [summary, setSummary] = useState({ total: 0, paid: 0, balance: 0, overdue: 0 })

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Row selection
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set())

  // Load masters options
  useEffect(() => {
    loadInstallmentOptions().then(() => {
      setOptions({
        companyOptions: COMPANY_OPTIONS,
        branchOptions: BRANCH_OPTIONS,
        courseOptions: COURSE_OPTIONS,
        schemeOptions: SCHEME_OPTIONS,
        academicYearOptions: ACADEMIC_YEAR_OPTIONS,
        batchOptions: BATCH_OPTIONS,
        subjectOptions: SUBJECT_OPTIONS,
        genderOptions: GENDER_OPTIONS,
        statusOptions: STATUS_OPTIONS,
        paymentStatusOptions: PAYMENT_STATUS_OPTIONS,
        datePeriodOptions: DATE_PERIOD_OPTIONS,
        pageSizeOptions: [25, 50, 75, 100],
      })
    })
  }, [])

  const queryParams = useMemo(
    () => ({
      searchField,
      searchText,
      companyFilter: company,
      branchFilter: branch,
      courseFilter: course,
      schemeFilter: scheme,
      academicYearFilter: academicYear,
      batchFilter: batch,
      subjectFilter: subject,
      genderFilter: gender,
      statusFilter: status,
      paymentStatusFilter: paymentStatus,
      fromDate,
      toDate,
      tab,
    }),
    [searchField, searchText, company, branch, course, scheme, academicYear, batch, subject, gender, status, paymentStatus, fromDate, toDate, tab]
  )

  const loadInstallments = async () => {
    setIsLoading(true)
    setError('')
    try {
      const [response, summaryResponse] = await Promise.all([
        listInstallments(queryParams),
        getInstallmentsSummary(queryParams),
      ])
      setRows(response.data || [])
      setSummary(summaryResponse || { total: 0, paid: 0, balance: 0, overdue: 0 })
      setCurrentPage(1)
      setSelectedRowIds(new Set())
    } catch (err) {
      console.error('Failed to load installments:', err)
      setError('Unable to fetch live installments. Please check network connection.')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadInstallments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, status, paymentStatus])

  const handleKpiCardClick = (target) => {
    if (kpiFilter === target) {
      // Toggle off to All
      setKpiFilter('All')
      setStatus('All')
      setPaymentStatus('All')
      return
    }

    setKpiFilter(target)
    if (target === 'All') {
      setStatus('All')
      setPaymentStatus('All')
    } else if (target === 'Paid') {
      setStatus('Paid')
      setPaymentStatus('Paid')
    } else if (target === 'Balance') {
      setStatus('Unpaid')
      setPaymentStatus('All')
    } else if (target === 'Overdue') {
      setStatus('All')
      setPaymentStatus('Overdue')
    }
  }

  const resetFilters = () => {
    setCompany('Select')
    setBranch('Select')
    setCourse('Select')
    setScheme('Select')
    setAcademicYear('Select')
    setBatch('Select')
    setSubject('Select')
    setGender('Select')
    setSearchField('Name')
    setSearchText('')
    setDatePeriod('period')
    setFromDate('')
    setToDate('')
    setStatus('All')
    setPaymentStatus('All')
    setKpiFilter('All')
    setCurrentPage(1)
    setTimeout(() => {
      loadInstallments()
    }, 10)
  }

  const toISODate = (date) => {
    const d = new Date(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  const handleDatePeriodChange = (preset) => {
    setDatePeriod(preset)
    if (preset === 'period') {
      setFromDate('')
      setToDate('')
      return
    }

    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const sunday = new Date(startOfDay)
    sunday.setDate(startOfDay.getDate() - ((startOfDay.getDay() + 6) % 7))

    const dayOffset = (days) => new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + days)

    const ranges = {
      today: [startOfDay, startOfDay],
      yesterday: [dayOffset(-1), dayOffset(-1)],
      current_week: [dayOffset(0), dayOffset(6)],
      current_month: [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)],
      current_year: [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)],
      last_week: [dayOffset(-7), dayOffset(-1)],
      last_month: [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)],
      last_year: [new Date(now.getFullYear() - 1, 0, 1), new Date(now.getFullYear() - 1, 11, 31)],
      next_week: [dayOffset(7), dayOffset(13)],
      next_month: [new Date(now.getFullYear(), now.getMonth() + 1, 1), new Date(now.getFullYear(), now.getMonth() + 2, 0)],
      next_year: [new Date(now.getFullYear() + 1, 0, 1), new Date(now.getFullYear() + 1, 11, 31)],
    }

    const range = ranges[preset]
    if (range) {
      setFromDate(toISODate(range[0]))
      setToDate(toISODate(range[1]))
    }
  }

  const handleTabChange = (nextTab) => {
    setTab(nextTab)
    setSearchParams({ tab: nextTab })
    setCurrentPage(1)
    setSelectedRowIds(new Set())
  }

  const handleOpenFeeLedger = (studentId) => {
    if (studentId) {
      navigate(`/admission/fees/${studentId}`)
    }
  }

  const handleEdit = (installmentId) => {
    navigate(`/admission/installments/entry?id=${installmentId}`)
  }

  const handleDelete = async (installmentId) => {
    const confirmed = await appConfirm({
      title: 'Delete Installment',
      message: 'Are you sure you want to delete this installment record? This action cannot be undone.',
      confirmText: 'Delete Installment',
      variant: 'danger',
    })
    if (!confirmed) return { success: false }

    try {
      await deleteInstallment(installmentId)
      appToast.success('Installment deleted successfully')
      await loadInstallments()
      return { success: true }
    } catch (err) {
      console.error('Delete installment error:', err)
      appToast.error('Failed to delete installment.')
      setError('Failed to delete installment.')
      return { success: false, error: err.message }
    }
  }

  const toggleRow = (id) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAllRows = (isChecked) => {
    if (isChecked) {
      setSelectedRowIds(new Set(paginatedRows.map((r) => r.id)))
    } else {
      setSelectedRowIds(new Set())
    }
  }

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return rows.slice(startIndex, startIndex + pageSize)
  }, [rows, currentPage, pageSize])

  const totalPages = Math.ceil(rows.length / pageSize) || 1

  return {
    state: {
      rows: paginatedRows,
      allRows: rows,
      totalCount: rows.length,
      isLoading,
      error,
      summary,
      tab,
      company,
      branch,
      course,
      scheme,
      academicYear,
      batch,
      subject,
      gender,
      searchField,
      searchText,
      fromDate,
      toDate,
      datePeriod,
      status,
      paymentStatus,
      kpiFilter,
      isSettingsOpen,
      currentPage,
      totalPages,
      pageSize,
      selectedRowIds,
      options,
    },
    actions: {
      setCompany,
      setBranch,
      setCourse,
      setScheme,
      setAcademicYear,
      setBatch,
      setSubject,
      setGender,
      setSearchField,
      setSearchText,
      setFromDate,
      setToDate,
      handleDatePeriodChange,
      setStatus,
      setPaymentStatus,
      handleKpiCardClick,
      setCurrentPage,
      setPageSize: (v) => { setPageSize(Number(v)); setCurrentPage(1) },
      search: loadInstallments,
      resetFilters,
      handleTabChange,
      openSettings: () => setIsSettingsOpen(true),
      closeSettings: () => setIsSettingsOpen(false),
      handleOpenFeeLedger,
      handleEdit,
      handleDelete,
      toggleRow,
      toggleAllRows,
    },
  }
}